import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';

import { FocusTrapDirective } from '../../directives/focus-trap/focus-trap.directive';
import { IconComponent } from '../icon/icon.component';
import { formatDobNumeric } from '../../utils/date-time/date-time.utils';
import { PatientFilter } from '../../models/patient-filter';
import { minLengthError, normalizeCriteria } from '../../utils/search-field/search-field.utils';

/** Matches an `MM/DD/YYYY` date, capturing month, day, and year. */
const US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * Field caps mirroring the API's own, so an over-long value is stopped at the keyboard instead of
 * coming back as a 400 the user can do nothing with.
 *
 * As of 2026-09-23, these match `Data.Contract/v1/Constraint/PatientConstraints.cs`
 * (`FirstNameMaximumLength` / `LastNameMaximumLength` = 125, `MedicalRecordNumberMaximumLength`
 * = 20). Hosts that send these as their own request fields inherit those same caps.
 */
const NAME_MAX_LENGTH = 125;
const MEDICAL_RECORD_NUMBER_MAX_LENGTH = 20;

/** True when `value` is a real `MM/DD/YYYY` calendar date (rejects e.g. `02/30/2020`). */
function isValidUsDate(value: string): boolean {
  const match = US_DATE_PATTERN.exec(value.trim());
  if (!match) {
    return false;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * True when `value` is a real `MM/DD/YYYY` date later than today. A date of birth in the future is
 * never valid, so it is rejected separately from a malformed date to give a clearer message.
 */
function isFutureUsDate(value: string): boolean {
  const match = US_DATE_PATTERN.exec(value.trim());
  if (!match) {
    return false;
  }

  const date = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date.getTime() > today.getTime();
}

/**
 * "Search by Patient" modal. Collects the structured patient identity fields (first/last name,
 * DOB, MRN) and emits them as a {@link PatientFilter} for the host screen to act on. What that
 * means is the host's business — the dialog only gathers the criteria.
 */
@Component({
  selector: 'app-patient-search-modal',
  standalone: true,
  imports: [FormsModule, NgxMaskDirective, IconComponent, FocusTrapDirective],
  templateUrl: './patient-search-modal.html',
  styleUrl: './patient-search-modal.scss',
})
export class PatientSearchModal implements OnInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The currently-applied filter, used to prefill the fields when the modal reopens. */
  readonly initialFilter = input<PatientFilter | null>(null);

  /**
   * Copy under the title, saying what the search does on the host screen. Required rather than
   * defaulted: a default would be one feature's wording living in `shared/`, and a host that
   * forgot to override it would silently advertise the wrong outcome.
   */
  readonly subtitle = input.required<string>();

  /**
   * How many fields the host's endpoint needs populated before it will run a search. Defaults to
   * 1 — one criterion is the least a search can be — and hosts raise it to match their own API:
   * the portal patient search rejects anything narrower than two criteria, so Access & Controls
   * passes 2 (see `PATIENT_ACCESS_MINIMUM_CRITERIA`). This mirrors the host endpoint's own rule
   * rather than adding a policy of its own — it is not a PHI control, and an MRN on its own is
   * blocked here because the API rejects it, not because it identifies too little. Gating here
   * rather than letting the request 400 means staff are told what is missing while the fields are
   * still in front of them.
   */
  readonly minimumCriteria = input(1);

  readonly applied = output<PatientFilter>();
  readonly cleared = output<void>();
  readonly closed = output<void>();

  /** Input caps matching the API's — see {@link NAME_MAX_LENGTH}. */
  readonly nameMaxLength = NAME_MAX_LENGTH;
  readonly medicalRecordNumberMaxLength = MEDICAL_RECORD_NUMBER_MAX_LENGTH;

  readonly firstName = signal('');
  readonly lastName = signal('');
  /** DOB as the user types it (`MM/DD/YYYY`); converted to ISO on search. */
  readonly dateOfBirth = signal('');
  readonly medicalRecordNumber = signal('');

  /**
   * Per-field validation errors, shown inline and used to gate the search. A blank field is
   * omitted (it is simply not part of the query); a populated field must satisfy its rule —
   * names and MRN must clear the shared minimum length (see {@link minLengthError}) and the DOB
   * must be a real `MM/DD/YYYY` date.
   */
  readonly firstNameError = computed(() => minLengthError(this.firstName(), 'First name'));
  readonly lastNameError = computed(() => minLengthError(this.lastName(), 'Last name'));
  readonly medicalRecordNumberError = computed(() =>
    minLengthError(this.medicalRecordNumber(), 'MRN'),
  );
  readonly dateOfBirthError = computed(() => {
    const value = this.dateOfBirth().trim();
    if (value.length === 0) {
      return null;
    }
    if (!isValidUsDate(value)) {
      return 'Enter a valid date as MM/DD/YYYY.';
    }
    if (isFutureUsDate(value)) {
      return 'Date of birth cannot be in the future.';
    }
    return null;
  });

  /**
   * Per-field "touched" flags, flipped on blur (or a failed search). Inline errors only render
   * once a field is touched so the user isn't warned mid-typing — they see the message when they
   * move on from the field, not on every keystroke.
   */
  readonly firstNameTouched = signal(false);
  readonly lastNameTouched = signal(false);
  readonly dateOfBirthTouched = signal(false);
  readonly medicalRecordNumberTouched = signal(false);

  /** How many fields currently carry a value — what {@link minimumCriteria} is measured against. */
  private readonly populatedCriteria = computed(
    () =>
      [this.firstName(), this.lastName(), this.dateOfBirth(), this.medicalRecordNumber()].filter(
        (value) => value.trim().length > 0,
      ).length,
  );

  /**
   * Message naming how many more fields the search needs, or null once it has enough. Shown next
   * to the disabled Search button so a host with a higher {@link minimumCriteria} doesn't read as
   * a dead button.
   */
  readonly criteriaCountError = computed(() => {
    const required = this.minimumCriteria();
    if (this.populatedCriteria() >= required) {
      return null;
    }
    return required === 1
      ? 'Enter at least one search field.'
      : `Enter at least ${required} search fields.`;
  });

  /** True when enough fields are populated and every populated field passes validation. */
  readonly canSearch = computed(
    () =>
      this.criteriaCountError() === null &&
      this.firstNameError() === null &&
      this.lastNameError() === null &&
      this.dateOfBirthError() === null &&
      this.medicalRecordNumberError() === null,
  );

  ngOnInit(): void {
    const filter = this.initialFilter();
    if (filter) {
      this.firstName.set(filter.firstName ?? '');
      this.lastName.set(filter.lastName ?? '');
      this.dateOfBirth.set(filter.dateOfBirth ? formatDobNumeric(filter.dateOfBirth) : '');
      this.medicalRecordNumber.set(filter.medicalRecordNumber ?? '');
    }

    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('input')?.focus();
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  close(): void {
    this.closed.emit();
  }

  clear(): void {
    this.firstName.set('');
    this.lastName.set('');
    this.dateOfBirth.set('');
    this.medicalRecordNumber.set('');
    this.firstNameTouched.set(false);
    this.lastNameTouched.set(false);
    this.dateOfBirthTouched.set(false);
    this.medicalRecordNumberTouched.set(false);
    this.cleared.emit();
  }

  search(): void {
    if (!this.canSearch()) {
      // Reveal any outstanding errors when the user tries to search (e.g. via Enter) so a blocked
      // search isn't a silent dead-end.
      this.firstNameTouched.set(true);
      this.lastNameTouched.set(true);
      this.dateOfBirthTouched.set(true);
      this.medicalRecordNumberTouched.set(true);
      return;
    }

    this.applied.emit({
      firstName: normalizeCriteria(this.firstName()),
      lastName: normalizeCriteria(this.lastName()),
      dateOfBirth: this.toIsoDate(this.dateOfBirth()),
      medicalRecordNumber: normalizeCriteria(this.medicalRecordNumber()),
    });
  }

  /** Convert an `MM/DD/YYYY` string to ISO `YYYY-MM-DD`; null when empty or malformed. */
  private toIsoDate(value: string): string | null {
    const match = US_DATE_PATTERN.exec(value.trim());
    if (!match) {
      return null;
    }

    const [, month, day, year] = match;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
}
